// De documenten bij een afstandsaanvraag: paspoort, foto, brief van de dierenarts.
//
// Twee calls die de generieke Refine dataProvider niet kan maken: de upload is multipart, en de bytes
// terughalen moet met de bearer (die in het geheugen zit, niet in een cookie — een <a href> of een
// <img src> krijgt hem dus niet mee). Zelfde patroon als src/api/dagafsluitingBijlage.ts.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export interface WachtlijstDocument {
  id: string;
  dierId: number | null;
  soort: string;
  bestandsnaam: string;
  contentType: string;
  grootte: number;
  aangemaaktOp: string;
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Een bestand dat het asiel zelf bij de aanvraag zet. */
export async function uploadWachtlijstDocument(
  aanvraagId: string,
  file: File,
  dierId?: number | null,
): Promise<WachtlijstDocument> {
  const form = new FormData();
  form.append("bestand", file);
  if (dierId != null) form.append("dierId", String(dierId));
  const res = await fetch(`${API_URL}/wachtlijst/${aanvraagId}/documenten`, {
    method: "POST",
    headers: authHeaders(), // geen Content-Type — de browser zet de multipart-boundary
    body: form,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as WachtlijstDocument;
}

/** De bytes ophalen, als blob — voor openen in een tabblad of downloaden. */
export async function fetchWachtlijstDocument(aanvraagId: string, documentId: string): Promise<Blob> {
  const res = await fetch(`${API_URL}/wachtlijst/${aanvraagId}/documenten/${documentId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.blob();
}

export async function verwijderWachtlijstDocument(aanvraagId: string, documentId: string): Promise<void> {
  const res = await fetch(`${API_URL}/wachtlijst/${aanvraagId}/documenten/${documentId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

/**
 * Het document openen in een nieuw tabblad.
 *
 * <p>Via een blob-url en niet via de gewone url: die laatste gaat zonder bearer de deur uit en levert een
 * leeg tabblad met een 401 op. De url wordt daarna weer vrijgegeven — niet meteen, want dan is het tabblad
 * nog niet klaar met laden.
 */
export async function openWachtlijstDocument(aanvraagId: string, documentId: string): Promise<void> {
  const blob = await fetchWachtlijstDocument(aanvraagId, documentId);
  const url = URL.createObjectURL(blob);
  globalThis.open(url, "_blank", "noopener");
  globalThis.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
